import { useCallback, useRef, useState } from "react";
import { transcribeReflection } from "../functions/api";

// Cap a single clip so we stay in Sarvam's "quick response" window (and keep
// the payload small). The recorder auto-stops at this point.
const MAX_MS = 25000;

const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4", // iOS Safari
  "audio/ogg;codecs=opus",
];

function pickMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  return MIME_CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t)) || "";
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(blob); // yields a data URL; backend strips the prefix
  });
}

export interface Dictation {
  recording: boolean;
  transcribing: boolean;
  error: string | null;
  supported: boolean;
  toggle: () => void;
}

/**
 * useDictation — tap to record a spoken reflection, tap again (or wait) to
 * stop; the transcript is handed back via `onText`.
 */
export function useDictation(onText: (text: string) => void): Dictation {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Show the button whenever the browser can record at all. The actual mic
  // gate (a secure https/localhost origin) is checked at record time, so on an
  // insecure origin the button still appears and explains itself rather than
  // silently vanishing.
  const supported = typeof MediaRecorder !== "undefined";

  const cleanupStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  }, []);

  const start = useCallback(async () => {
    setError(null);
    if (!supported) {
      setError("Voice isn't supported in this browser.");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Voice needs a secure (https) link — it won't work over a local IP.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = pickMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        setRecording(false);
        cleanupStream();

        const type = recorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size === 0) return;

        setTranscribing(true);
        try {
          const dataUrl = await blobToBase64(blob);
          const text = await transcribeReflection(dataUrl, type);
          if (text) onText(text);
          else setError("Didn't catch that — try again?");
        } catch {
          setError("Couldn't transcribe that — try again in a moment.");
        } finally {
          setTranscribing(false);
        }
      };

      recorder.start();
      setRecording(true);
      timerRef.current = setTimeout(stop, MAX_MS);
    } catch (err: any) {
      cleanupStream();
      setRecording(false);
      if (err?.name === "NotAllowedError" || err?.name === "SecurityError") {
        setError("Mic permission is needed to speak your reflection.");
      } else {
        setError("Couldn't reach the microphone.");
      }
    }
  }, [supported, stop, onText]);

  const toggle = useCallback(() => {
    if (transcribing) return;
    if (recording) stop();
    else start();
  }, [recording, transcribing, start, stop]);

  return { recording, transcribing, error, supported, toggle };
}
